import { useEffect, useRef, useState } from "react";

import { CommonTableHeaderComponent } from "@/Common/Components/Tables/CommonTableHeaderComponent";
import { MyleTable } from "@/Common/Components/Tables/MyleTable";
import { MyleTableColumnOrderControlv2 } from "@/Common/Components/Tables/MyleTableColumnOrderControlv2";
import { SimplePagerControl } from "@/Common/Components/Tables/SimplePagerControl";
import { displayToast, MessageToast } from "@/Common/Components/Toast/Toast";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { Guid } from "@/Common/Models/Guid";
import { PharmacyOrderStatus } from "@/Common/Models/UI/Enums";
import { TableDataSourceItem } from "@/Common/Models/UI/Tables/ITableDataSourceItem";
import * as LocalOperations from "@/Common/Operations/operations.local";
import { PharmacyOrdersClient } from "../Api/PharmacyOrdersClient";
import { PharmacyOrderHelper } from "../Helper/PharmacyOrderHelper";
import { usePharmacyOrdersTable } from "../Hooks/usePharmacyOrdersTable";
import { usePharmacyOrdersTableData } from "../Hooks/usePharmacyOrdersTableData";
import { PharmacyOrderModel } from "../Models/PharmacyOrderModel";
import { PharmacyOrdersFilterSettingsModel } from "../Models/PharmacyOrdersFilterSettingsModel";
import { PharmacyOrdersTableSettingsModel } from "../Models/PharmacyOrdersTableSettingsModel";
import { PharmacyOrdersFilterSettings } from "./PharmacyOrdersFilterSettings";

import styles from "./PharmacyOrdersInbox.module.scss";

interface IPharmacyOrdersInboxProps {
    readonly onRowClickCallback: (orderId: Guid) => void;
}

function PharmacyOrdersInbox({ onRowClickCallback }: IPharmacyOrdersInboxProps): JSX.Element {
    const { getText } = useTranslation();
    const barCodeValidator = /[0-9a-z]{3,5} [0-9]{5} [0-9a-z]{6}/gi;

    const [filterSetting, setFilterSetting] = useState(() =>
        PharmacyOrdersFilterSettingsModel.fromResponseModel(cacheStore.UserSettings.PharmacyOrdersFilterSettings) ?? new PharmacyOrdersFilterSettingsModel()
    );
    const [tableSetting, setTableSetting] = useState(pharmacyOrdersStore?.pharmacyOrdersTableSettings ?? new PharmacyOrdersTableSettingsModel());
    const [isFilterSettingOpen, setIsFilterSettingOpen] = useState(false);
    const [isColumnOrderControlOpen, setIsColumnOrderControlOpen] = useState(false);
    const [isAllCheckboxChecked, setIsAllCheckboxChecked] = useState(false);
    const [pharmacyOrdersIds, setPharmacyOrdersIds] = useState<string[]>([]);
    const [pharmacyOrdersTableItems, setPharmacyOrdersTableItems] = useState<TableDataSourceItem<PharmacyOrderModel>[]>([]);
    const [hasSearchBeenModified, setHasSearchBeenModified] = useState<boolean>(false);
    const { columns, tableRenderOptions, columnConfigurationModels, saveOptionKey } = usePharmacyOrdersTable(
        tableSetting,
        [isAllCheckboxChecked, setIsAllCheckboxChecked],
        (orderId, isChecked) => onToggleIsChecked(orderId, isChecked),
        (newOrderType, newOrderDirection) => {
            setTableSetting(p => new PharmacyOrdersTableSettingsModel({ ...p, queryOrderType: newOrderType, queryOrderDirection: newOrderDirection }));
        },
        (orderId, shouldArchive) => onToggleArchiveRestore(orderId, shouldArchive),
        (orderId, orderStatus) => onCompleteOrderAsync(orderId, orderStatus),
        orderId => onSendManualReminder(orderId),
        orderId => filePharmacyOrder(orderId)
    );

    const onToggleIsChecked = (orderId: string, isChecked: boolean): void => {
        setPharmacyOrdersTableItems(p => p.map(item => {
            if (item.Model.orderId === orderId) {
                const newModel = new TableDataSourceItem(item.Model);
                newModel.IsSelected = isChecked;
                return newModel;
            }

            return item;
        }));
    };

    const onToggleArchiveRestore = async (orderId: string, shouldArchive: boolean): Promise<void> => {
        const isUpdated = await PharmacyOrdersClient.updatePharmacyOrdersArchiveRestore([orderId], shouldArchive);

        if (!isUpdated) {
            return;
        }

        fetchPharmacyOrders();
    };

    const onCompleteOrderAsync = async (orderId: string, orderStatus: PharmacyOrderStatus): Promise<void> => {
        const sendNotification = PharmacyOrderHelper.hasSendNotification(orderStatus);
        await PharmacyOrdersClient.completePharmacyOrdersAsync([orderId], sendNotification);
        fetchPharmacyOrders();
    };

    const onSendManualReminder = async (orderId: string): Promise<void> => {
        const isReminderSent = await PharmacyOrdersClient.sendSmsReminderToPatient(orderId);

        if (isReminderSent) {
            setPharmacyOrdersTableItems(prevItems =>
                prevItems.map(item => {
                    if (item.Model.orderId === orderId) {
                        const newModel = new TableDataSourceItem(item.Model);
                        newModel.Model.patientReminderSentDate = new Date();
                        return newModel;
                    }
                    return item;
                }));

            displayToast(MessageToast.Success, getText("PharmacyOrders_ReminderSentLabel"));
        }
        else
            displayToast(MessageToast.Error, getText("PharmacyOrders_MessageReminderFailed"));
    };

    const filePharmacyOrder = async (orderId: Guid): Promise<void> => {
        const isPharmacyOrderFiled = await PharmacyOrdersClient.filePharmacyOrder(orderId);

        if (!isPharmacyOrderFiled) {
            return;
        }

        fetchPharmacyOrders();
        displayToast(MessageToast.Success, getText("PharmacyOrders_OrderHasBeenFiledLabel"));
    };

    const { data, totalCount, fetchPharmacyOrders } = usePharmacyOrdersTableData(filterSetting, tableSetting);
    const previousData = useRef<PharmacyOrderModel[]>(data);

    useEffect(() => {
        $(window).trigger("resize");
    }, []);

    useEffect(() => {
        if (data.length === 1 && hasSearchBeenModified && previousData.current !== data) {
            onRowClickCallback(data[0].orderId);
            return;
        }

        setPharmacyOrdersTableItems(data.map(p => new TableDataSourceItem(p)));
        setPharmacyOrdersIds(data.map(p => p.orderId));
        previousData.current = data;
    }, [data, hasSearchBeenModified, onRowClickCallback]);

    useEffect(() => {
        setPharmacyOrdersTableItems(
            p => p.map(item => {
                const newModel = new TableDataSourceItem(item.Model);
                newModel.IsSelected = isAllCheckboxChecked;
                return newModel;
            })
        );
    }, [isAllCheckboxChecked]);

    useEffect(() => {
        sessionStore.dispatch(LocalOperations.PharmacyOrders.SetPharmacyOrdersIds, { pharmacyOrdersIds, totalCount });
    }, [pharmacyOrdersIds, totalCount]);

    useEffect(() => {
        sessionStore.dispatch(LocalOperations.PharmacyOrders.SetIFilterSettings, { filterSetting });
    }, [filterSetting]);

    useEffect(() => {
        sessionStore.dispatch(LocalOperations.PharmacyOrders.SetTableSettings, { tableSetting });
    }, [tableSetting]);

    const selectedArchivableItems = pharmacyOrdersTableItems.filter(p =>
        p.IsSelected &&
        !p.Model.isArchived &&
        p.Model.orderStatus !== PharmacyOrderStatus.Contacted
    );
    const selectedCompletableItems = pharmacyOrdersTableItems.filter(p =>
        p.IsSelected &&
        p.Model.orderStatus !== PharmacyOrderStatus.Completed &&
        !p.Model.isArchived &&
        p.Model.orderStatus !== PharmacyOrderStatus.Contacted
    );

    const setSearchBoxModifiesStatus = (text: string): boolean =>
        !!text?.length && (barCodeValidator.test(text) || (cacheStore.ClinicSettings.EnablePrescriptionBarcode6Digits && text.length === 6));

    return (
        <div className={styles.container}>
            <div>
                <CommonTableHeaderComponent
                    title={getText("PharmacyOrders_OrderManagementLabel")}
                    searchText={tableSetting.searchText}
                    searchPlaceholder={getText("SearchLabel")}
                    onSearch={text => {
                        setTableSetting(p => new PharmacyOrdersTableSettingsModel({ ...p, searchText: text }));
                        setHasSearchBeenModified(() => setSearchBoxModifiesStatus(text));
                    }}
                    onRefreshClick={() => fetchPharmacyOrders()}
                    onSettingsClick={() => setIsFilterSettingOpen(isFilterSettingOpen => !isFilterSettingOpen)}
                    enableSearchBoxAutoFocus={true}
                />
                {isFilterSettingOpen && (
                    <PharmacyOrdersFilterSettings
                        cachedSettings={filterSetting}
                        onSaveChanges={(orderSettings, isSaveSettingsChecked) => {
                            if (isSaveSettingsChecked) {
                                PharmacyOrdersClient.savePharamcyOrderFilterSettings(orderSettings);
                            }
                            cacheStore.UserSettings.PharmacyOrdersFilterSettings = PharmacyOrdersFilterSettingsModel.toResponseModel(orderSettings);
                            setFilterSetting(orderSettings);
                            setIsFilterSettingOpen(false);
                            setTableSetting(p => new PharmacyOrdersTableSettingsModel({ ...p, currentPageNumber: 1 }));
                        }}
                        onClose={() => setIsFilterSettingOpen(false)}
                        onColumnOrderingPopupOpenClick={() => {
                            setIsFilterSettingOpen(false);
                            setIsColumnOrderControlOpen(true);
                        }}
                    />
                )}
                {isColumnOrderControlOpen && (
                    <MyleTableColumnOrderControlv2
                        TableColumns={columnConfigurationModels}
                        SaveOptionskeyTarget={saveOptionKey}
                        ContainerId="PharmacyOrdersColumnOrderControl"
                        closeColumnOrderPopup={() => setIsColumnOrderControlOpen(false)}
                    />
                )}
            </div>
            <div id="posref" className="ws-scrollable">
                <div className="myle-doc-tb main-content myle-form">
                    <MyleTable
                        TableRenderOptions={tableRenderOptions}
                        TableId="pharmacy-orders-myle-table"
                        Columns={columns}
                        DataSource={Immutable.List(pharmacyOrdersTableItems)}
                        SaveOptionskeyTarget={saveOptionKey}
                        RowClickFunction={model => onRowClickCallback(model.Model.orderId)}
                    />
                </div>
            </div>
            <div className={`ws-footer ${styles.footer}`}>
                <div className="col-lg-6 col-md-6 col-sm-6">
                    <SimplePagerControl
                        PageSize={filterSetting.queryPageSize}
                        TotalRows={totalCount}
                        CurrentPage={tableSetting.currentPageNumber}
                        changePageFunction={nextPageNumber =>
                            setTableSetting(p =>
                                new PharmacyOrdersTableSettingsModel({ ...p, currentPageNumber: nextPageNumber })
                            )
                        }
                    />
                </div>
                <div className="col-lg-6 col-md-6 col-sm-6 text-right">
                    <button
                        className="btn btn-primary push-right-5"
                        onClick={async () => {
                            const selectedOrderIds = selectedCompletableItems.map(p => p.Model.orderId);
                            await PharmacyOrdersClient.completePharmacyOrdersAsync(selectedOrderIds, false);
                            fetchPharmacyOrders();
                        }}
                        disabled={!selectedCompletableItems.length}
                    >
                        {getText("PharmacyOrders_CompleteWithoutNotifyingLabel")}
                    </button>
                    <button
                        className="btn btn-primary push-right-5"
                        onClick={async () => {
                            const selectedOrderIds = selectedArchivableItems.map(p => p.Model.orderId);
                            await PharmacyOrdersClient.updatePharmacyOrdersArchiveRestore(selectedOrderIds, true);
                            fetchPharmacyOrders();
                        }}
                        disabled={!selectedArchivableItems.length}
                    >
                        {getText("ArchiveLabel")}
                    </button>
                </div>

            </div>
        </div>
    );
}

export { PharmacyOrdersInbox };
